"use client";

import type { ButtonHTMLAttributes, ReactNode } from "react";

export type GlassButtonKind = "primary" | "secondary" | "destructive";
export type GlassButtonSize = "xs" | "sm" | "md" | "lg";

export interface GlassButtonProps
	extends ButtonHTMLAttributes<HTMLButtonElement> {
	readonly kind?: GlassButtonKind;
	readonly size?: GlassButtonSize;
	readonly symbol?: ReactNode;
	readonly expands?: boolean;
	readonly isLoading?: boolean;
	/** When `false`, the button is disabled. Defaults to enabled. */
	readonly isEnabled?: boolean;
}

/**
 * GlassButton — primary call-to-action button. Sizes map to the contract
 * heights (xs=34 / sm=40 / md=44 / lg=50). Supports a leading symbol, a
 * loading state, and full-width (`expands`) layout.
 */
export function GlassButton({
	children,
	className = "",
	kind = "secondary",
	size = "md",
	symbol,
	expands = false,
	isLoading = false,
	isEnabled,
	disabled,
	...props
}: Readonly<GlassButtonProps>) {
	const resolvedDisabled = disabled || isLoading || isEnabled === false;
	return (
		<button
			className={`ds-button ds-button--${kind} ds-button--${size}${expands ? " ds-button--expands" : ""} ${className}`.trim()}
			disabled={resolvedDisabled}
			{...props}
		>
			{isLoading ? (
				<span
					className="ds-button__symbol"
					aria-hidden="true"
					style={{ animation: "ds-spin 0.8s linear infinite" }}
				>
					○
				</span>
			) : symbol === undefined ? null : (
				<span className="ds-button__symbol" aria-hidden="true">
					{symbol}
				</span>
			)}
			<span>{children}</span>
		</button>
	);
}
